import { LOCALES } from "common/constants";
import { getSchemaMarkup } from "common/FunctionUtils";
import { blobURL } from "common/StringUtils";
import Footer from "components/layout/Footer";
import Header from "components/layout/Header";
import React, { useEffect } from "react";
import { Button, Container } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

export const InvoicingSelfEmployed = ({ dashboardFooter }) => {
  const schemaMarkup = getSchemaMarkup("fakturera-som-privatperson_egenanstalld");
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);

  let language = localStorage.getItem("i18nextLng");
  const invoicingSelfEmployedBanner = `${blobURL}/invoicing-self-employed.png`;

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  return (
    <div>
      <Helmet>
        {schemaMarkup && (
          <script type="application/ld+json">
            {JSON.stringify(schemaMarkup)}
          </script>
        )}
        <title>
          {t("Invoicing as a private individual and self-employed | Invozio")}
        </title>
        <meta
          name="description"
          content={t(
            "Invoice as a private individual and self-employed without starting a company. Learn about the rules, fees, and how to quickly get paid for your assignments"
          )}
        />
        {language === LOCALES.EN_US ? (
          <link
            rel="canonical"
            href="https://invozio.com/invoicing-as-an-individual_self-employed"
          />
        ) : (
          <link
            rel="canonical"
            href="https://invozio.com/fakturera-som-privatperson_egenanstalld"
          />
        )}
      </Helmet>

      <Header />
      <Container className="p-0 self_new negotiate neg_rspn">
        <div className="guidance">
          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center title">
                <div className="col-md-6">
                  <div className="page-heading">
                    <h2 className="purple">
                      {t("Invoicing as an individual self-employed")}
                    </h2>

                    {!user && (
                      <Button
                        className="mt-2 create-account filled home_create_account"
                        onClick={() => {
                          navigate(
                            language === LOCALES.EN_US
                              ? `/emailregister?lang=${LOCALES.EN_US}`
                              : `/e-postregister?lang=${LOCALES.SV_SE}`
                          );
                          sessionStorage.setItem("accountType", 1);
                        }}
                      >
                        {t("Create Free Account")}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="image-group text-right">
                    <img
                      height={300}
                      src={invoicingSelfEmployedBanner}
                      alt="invoicing-self-employed"
                      loading="lazy"
                      style={{
                        textAlign: "right",
                        maxWidth: "100%",
                        height: "auto",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h1 className="purple">
                    {t("Invoice as a private individual and self-employed")}
                  </h1>
                  <p>
                    {t(
                      "In Sweden, you can legally invoice as a private individual and self-employed. This is an attractive option for many who do not want to start a company but still want to be paid for services or goods. By using an invoicing service that allows invoicing without a company, such as Invozio, you can focus on completing the work while the service handles taxes and fees. However, before you start, there are some important aspects to consider when invoicing as self-employed. Read on to understand what it means to invoice as a private individual and how you can get started."
                    )}
                  </p>
                  <blockquote className="blockquote_txt">
                    {t(
                      "“We have the most innovative platform and the lowest prices on the market. We evolve in line with the needs and challenges of our users,” says Didrik Selder, CEO at Invozio."
                    )}
                  </blockquote>

                  <Link
                    className="mb-2"
                    to={`${
                      language === LOCALES.EN_US
                        ? `/self-employment?lang=${LOCALES.EN_US}`
                        : `/egenanstallning?lang=${LOCALES.SV_SE}`
                    }`}
                    style={{ textDecoration: "underline", fontSize: 16 }}
                  >
                    {t("Read more about how you can become self-employed")}
                  </Link>
                </div>
              </div>
            </div>
          </section>

          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h2 className="purple">
                    {t(
                      "The number of self-employed freelancers is growing in Sweden"
                    )}
                  </h2>
                  <p>
                    {t(
                      "Freelancing has become increasingly popular in Sweden. A new survey shows that 3 out of 10 people work as freelancers or consultants. With this growing number of freelancers, the question often arises: Can you invoice as a private individual without a company? This article provides a clear overview of what you need to know about invoicing without a company. We cover important aspects such as F-tax, hobby activities, VAT, and how payments work for self-employed individuals. Whether you're considering starting to freelance or are already underway, valuable information is here for you."
                    )}
                  </p>
                </div>
              </div>
            </div>
          </section>
          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h2 className="purple">
                    {t(
                      "Legality and regulation for self-employed private individuals"
                    )}
                  </h2>
                  <p>
                    {t(
                      "Billing as a private individual in Sweden is legal, both for services and goods you sell. However, there are certain rules to follow. Invoicing as a private individual must not be part of a regular business operation. It is important to understand that if your freelance assignments become frequent, you may need to register a sole proprietorship. This also requires you to obtain an F-tax certificate, which is necessary to receive payments as a business owner. You can find more information about this at"
                    )}
                  </p>
                  <Link
                    className="mb-2"
                    to={`${
                      language === LOCALES.EN_US
                        ? `/freelance-consultant-salary-invoice-without-a-company?lang=${LOCALES.EN_US}`
                        : `/frilans-konsult-lon-fakturera-utan-foretag?lang=${LOCALES.SV_SE}`
                    }`}
                    style={{ textDecoration: "underline" }}
                  >
                    {t(
                      "Invozio's guide on F-tax and invoicing without a company"
                    )}
                  </Link>
                </div>
              </div>
            </div>
          </section>
          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h2 className="purple">
                    {t("What does it mean to invoice as a private individual?")}
                  </h2>
                  <p>
                    {t(
                      "Invoicing as a private individual means that you can get paid for your services without having to register a business. You perform the work and use a service like Invozio to invoice the customer. Invozio then handles everything from sending the invoice to ensuring that taxes and fees are paid. Once the payment from the customer is received, you get your salary, with all necessary fees already deducted. This means you avoid the administrative burden that self-employed individuals often face while still complying with all Swedish tax and legal regulations."
                    )}
                  </p>
                  <p className="quote_text">
                    {t(
                      '"Invoicing as a private individual through Invozio makes it easy to get paid without having to start a business. We take care of the entire process—from sending the invoice to paying taxes and fees. This way, you can focus on your work while we ensure that everything is handled according to Swedish rules and regulations." – Didrik Selder, CEO at Invozio.'
                    )}
                  </p>
                </div>
              </div>
            </div>
          </section>
          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h2 className="purple">
                    {t("What is Invozio and how does it work?")}
                  </h2>
                  <p>
                    <Link
                      className="mr-1 mb-2"
                      to={`${
                        language === LOCALES.EN_US
                          ? `/about-invozio?lang=${LOCALES.EN_US}`
                          : `/om-invozio?lang=${LOCALES.SV_SE}`
                      }`}
                      style={{ textDecoration: "underline" }}
                    >
                      {t("Invozio")}
                    </Link>
                    {t(
                      "is an invoicing service that enables private individuals to invoice customers without a registered business. The process is simple: you sign up on their platform, fill in details about the work and the customer you want to invoice, and Invozio takes care of the rest. The invoice is sent to the customer, and once the payment has been received, your salary is paid out, with deductions for social fees, taxes, and Invozio's fee. For more information, visit"
                    )}
                  </p>
                  <Link
                    className="mb-2"
                    to={`${
                      language === LOCALES.EN_US
                        ? `/how-much-should-I-invoice-as-a-freelancer?lang=${LOCALES.EN_US}`
                        : `/hur-mycket-ska-jag-fakturera-som-frilansare?lang=${LOCALES.SV_SE}`
                    }`}
                    style={{ textDecoration: "underline" }}
                  >
                    {t("Invozio's Salary Guide")}
                  </Link>
                </div>
              </div>
            </div>
          </section>
          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h3 className="purple">
                    {t("Why choose to invoice without a company?")}
                  </h3>
                  <p>
                    {t(
                      "Running a business involves responsibility and time-consuming administration. Many who work on a freelance basis or take on temporary assignments prefer to invoice as a private individual through a service like Invozio. This way, you don't have to worry about tax regulations, bookkeeping, or VAT registration. Instead, you can focus on the actual work, knowing that everything is handled correctly in the background. Additionally, you receive the same social safety net as an employee, such as pensionable income and sickness benefits."
                    )}
                  </p>
                  <Link
                    className="mb-2"
                    to={`${language === LOCALES.EN_US ? `/?lang=${LOCALES.EN_US}` : `/?lang=${LOCALES.SV_SE}`}`}
                    style={{ textDecoration: "underline" }}
                  >
                    {t(
                      "Learn more about how Invozio works and invoice without a company"
                    )}
                  </Link>
                </div>
              </div>
            </div>
          </section>
          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h2 className="purple">
                    {t("The advantages of invoicing as a private individual")}
                  </h2>
                  <p>
                    {t(
                      "There are many advantages to using an invoicing service as a private individual:"
                    )}
                  </p>
                </div>
                <div>
                  <ul>
                    <li className="self_emp_list">
                      <strong className="mr-1">
                        {t("No business registration required:")}
                      </strong>
                      {t(
                        "You can start invoicing without needing to establish a company."
                      )}
                    </li>
                    <li className="self_emp_list">
                      <strong className="mr-1">
                        {t("Snabb och enkel process:")}
                      </strong>
                      {t(
                        "Immediately after registration, you can invoice your clients."
                      )}
                    </li>
                    <li className="self_emp_list">
                      <strong className="mr-1">
                        {t("Automatic tax management:")}
                      </strong>
                      {t("Invozio handles all taxes and social fees for you.")}
                    </li>
                    <li className="self_emp_list">
                      <strong className="mr-1">
                        {t("Pensionable income:")}
                      </strong>
                      {t(
                        "Since Invozio pays your social fees, you are entitled to pensionable income and other social benefits, such as sickness benefits."
                      )}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h2 className="purple">
                    {t("Risks of self-employment and how Invozio helps")}
                  </h2>
                  <p>
                    {t(
                      "Self-employment can involve a lot of risks. You have to manage taxes, VAT, and bookkeeping yourself, and any mistakes can lead to fines or other legal issues. By using Invozio to invoice as a private individual, you avoid these risks. They handle the entire administrative process, making it a safe solution for those who don’t want the responsibility of running a business."
                    )}
                  </p>
                </div>
              </div>
            </div>
          </section>
          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h2 className="purple">
                    {t("How does invoicing work for private individuals?")}
                  </h2>
                  <p>
                    {t(
                      "To start invoicing as a private individual through Invozio, you just need to follow a few simple steps:"
                    )}
                  </p>
                </div>
                <div>
                  <ul>
                    <li className="self_emp_list">
                      {t("Register on Invozio's platform.")}
                    </li>
                    <li className="self_emp_list">
                      {t("Create a profile with your personal information.")}
                    </li>
                    <li className="self_emp_list">
                      {t(
                        "Add the customer's details and specify the work you have completed."
                      )}
                    </li>
                    <li className="self_emp_list">
                      {t("Send the invoice through the platform.")}
                    </li>
                    <li className="self_emp_list">
                      {t(
                        "When the customer has paid, you will receive your salary after taxes and fees have been deducted, or you can choose express payment."
                      )}
                    </li>
                  </ul>

                  <blockquote className="blockquote_txt">
                    {t(
                      "“The process is simple and quick, which means you don’t have to worry about complicated tax issues, administration, and bookkeeping,” says Didrik Selder, CEO at Invozio."
                    )}
                  </blockquote>
                </div>
              </div>
            </div>
          </section>
          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h2 className="purple">
                    {t("Taxes and fees for the self-employed")}
                  </h2>
                  <p>
                    {t(
                      "When you invoice as a private individual, you need to consider taxes and social fees, but we at"
                    )}
                    <Link
                      className="ml-1 mb-2"
                      to={`${language === LOCALES.EN_US ? `/?lang=${LOCALES.EN_US}` : `/?lang=${LOCALES.SV_SE}`}`}
                      style={{ textDecoration: "underline" }}
                    >
                      {t("Invozio")}
                    </Link>
                    <span className="ml-1">
                      {t(
                        "handle this for you. We deduct taxes and social fees from your invoice before you get paid. In this way, you can be sure that you are complying with Swedish laws and regulations. You don’t have to worry about any issues with the Swedish Tax Agency, and everything is handled smoothly. For more information on employer contributions, visit"
                      )}
                    </span>
                    <a
                      className="ml-1 mb-2"
                      target="_blank"
                      href="https://verksamt.se/personal-rekrytering/vad-kostar-det/redovisa-och-betala-arbetsgivaravgifter"
                      style={{ textDecoration: "underline" }}
                      rel="noopener noreferrer"
                    >
                      {t("Verksamt.se")}
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="guide-section">
            <div className="container">
              <div className="purple row align-items-center">
                <div className="guide">
                  <h2 className="purple">{t("FAQ")}</h2>
                  <p>
                    {t(
                      "Here we have compiled the most common questions we receive from freelancers, consultants, and others who do not have a company but want to invoice private individuals or businesses."
                    )}
                  </p>
                </div>
                <div>
                  <h3 className="purple">
                    {t(
                      "What types of jobs can I invoice as a private individual through Invozio?"
                    )}
                  </h3>
                  <p>
                    <strong>{t("Answer")}: </strong>
                    {t(
                      "You can invoice for most services and assignments as long as they comply with Invozio's guidelines and are legal."
                    )}
                  </p>
                </div>

                <div>
                  <h3 className="purple">
                    {t("How do taxes work for private individuals?")}
                  </h3>
                  <p>
                    <strong>{t("Answer")}: </strong>
                    {t(
                      "When you invoice as a private individual through Invozio, all taxes and fees are automatically deducted from your amount. This means you receive a net salary, and you don’t have to worry about tax filing obligations."
                    )}
                  </p>
                </div>

                <div>
                  <h3 className="purple">
                    {t("Do you need to register for VAT?")}
                  </h3>
                  <p>
                    <strong>{t("Answer")}: </strong>
                    {t(
                      "No, you do not need to register for VAT if you invoice through Invozio. They handle VAT matters and ensure that the invoice sent to the customer includes the correct VAT."
                    )}
                  </p>
                </div>

                <div>
                  <h3 className="purple">
                    {t(
                      "Can you invoice as a private individual for all types of jobs?"
                    )}
                  </h3>
                  <p>
                    <strong>{t("Answer")}: </strong>
                    {t(
                      "Yes, you can invoice for most types of services and assignments as long as they are legal and comply with Invozio's guidelines. This can include everything from freelance work to smaller consulting assignments."
                    )}
                  </p>
                </div>

                <div>
                  <h3 className="purple">{t("How do social fees work?")}</h3>
                  <p>
                    <strong>{t("Answer")}: </strong>
                    {t(
                      "Invozio deducts social fees from your earnings and pays them to the Swedish Tax Agency. This entitles you to benefits such as pensionable income and sickness benefits, just as if you were an employee."
                    )}
                  </p>
                </div>

                {!user && (
                  <Button
                    className="self_emp create-account filled home_create_account"
                    onClick={() => {
                      navigate(
                        language === LOCALES.EN_US
                          ? `/emailregister?lang=${LOCALES.EN_US}`
                          : `/e-postregister?lang=${LOCALES.SV_SE}`
                      );
                      sessionStorage.setItem("accountType", 1);
                    }}
                  >
                    {t("Register and send your invoices")}
                  </Button>
                )}
              </div>
            </div>
          </section>
        </div>
      </Container>
      <Footer dashboardFooter={dashboardFooter} />
    </div>
  );
};
